import { lazy, type ComponentType } from "react";

/**
 * Lazy-load with one retry + guarded page-reload fallback.
 * Handles stale chunk errors after Vercel deploys.
 *
 * 2026-07-05 hardening: the old behavior was retry → unconditional
 * window.location.reload(). Two failure modes slipped through:
 *   1. Reload LOOP — when the failure isn't a stale deploy but a bad network
 *      path (observed live: QUIC errors dropping asset fetches; the service
 *      worker converts the rejected fetch into an empty 408), every reload
 *      fails the same way.
 *   2. Silent soft-brick — a lazy DIALOG that never resolves leaves the Radix
 *      overlay mounted with no content: a full-screen black veil with no
 *      affordance (reported on /generate at the daily limit).
 * Now: reload at most once per 60s window; if the chunk still fails after a
 * recent reload, resolve to a small recovery card with a working Refresh
 * button so the user is never stranded under a veil.
 *
 * The constraint is `ComponentType<any>` (the same idiom `@types/react`'s own
 * `lazy` uses), NOT `ComponentType<unknown>`. A component declaring required
 * props is not assignable to `ComponentType<unknown>` (props are contravariant),
 * so `T` collapses and every lazy-loaded component's props erase to
 * `IntrinsicAttributes` — breaking ~30 call sites across Generate/Playlists.
 * `any` only bounds the generic here; the concrete component type `T` is
 * preserved, so prop types at each usage stay exact.
 */

const RELOAD_GUARD_KEY = "spotivibly_chunk_reload_at";
const RELOAD_GUARD_WINDOW_MS = 60_000;

function recentlyReloaded(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0) < RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage blocked — never auto-reload (can't guard the loop),
    // go straight to the recovery card.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch { /* best-effort */ }
}

/** Minimal recovery surface — must not depend on any lazy chunk. */
function ChunkLoadError() {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/90 p-6">
      <div className="max-w-sm rounded-xl border border-border bg-card p-6 text-center space-y-3">
        <p className="text-sm font-semibold text-foreground">
          Couldn&apos;t load this part of the app
        </p>
        <p className="text-xs text-muted-foreground">
          Your connection dropped while fetching an update. Check your network and refresh.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch(() =>
      importFn().catch(() => {
        if (!recentlyReloaded()) {
          markReloaded();
          window.location.reload();
          return new Promise<{ default: T }>(() => {}); // never resolves (page reloads)
        }
        // Reloaded recently and the chunk STILL fails — bad network, not a
        // stale deploy. Surface recovery instead of a dead veil.
        return { default: ChunkLoadError as unknown as T };
      })
    )
  );
}
